import { AMPLITUDE_EVENT } from '@sonar-software/sonar-analytics';
import { NOTIFICATIONS_COPY } from '@sonar-software/sonar-ts-constants';
import { useAmplitude } from './useAmplitude';
import { useApiResource } from './useApiResource';
import { useAppNotifications } from './app';
import { useAuthToken } from './useAuthToken';
import { useFeatureFlag } from './useFeatureFlags';
import { AUTODOC_TYPE_LABELS, AutodocType, FeatureFlag } from '@sonar-software/sonar-core';
import { ErrorOrFailStatusCode, SuccessStatusCode } from '@sonar-software/sonar-http';
import { getInitialEditorState, hasContent } from '@ui/Editor';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface UseSuggestionEditorOptions {
	autodocType: AutodocType;
	isReadOnly?: boolean;
	richText?: boolean;
	sonarObjectId: number;
	value?: string | null;
}

export interface UseSuggestionEditorResult {
	accept: () => void;
	accepted: boolean;
	editorValue: string;
	hasError: boolean;
	isEditing: boolean;
	loading: boolean;
	request: () => void;
	reset: () => void;
	value?: string;
}

export function useSuggestionEditor(options: UseSuggestionEditorOptions): UseSuggestionEditorResult {
	const { autodocType, isReadOnly, richText, sonarObjectId, value } = options;
	const [ accepted, setAccepted ] = useState(false);
	const [ hasError, setError ] = useState(false);
	const { isReadOnly: isUserReadOnly } = useAuthToken();
	const { errorNotification } = useAppNotifications();
	const enabled = useFeatureFlag(FeatureFlag.Autodoc);
	const log = useAmplitude();

	const isEmpty = richText ? !hasContent(getInitialEditorState(value || '')) : !value;

	const {
		reset: apiReset,
		request: apiRequest,
		resource,
		response,
		loading
	} = useApiResource<{ text: string }>(
		`v3/core/sonar-objects/${sonarObjectId}/suggestions/${autodocType}`,
		null,
		true
	);

	const suggestionValue = useMemo(() => {
		if (!resource?.text) {
			return undefined;
		}

		let cleanValue = resource.text.trim();

		if (cleanValue.startsWith('"') && cleanValue.endsWith('"')) {
			cleanValue = cleanValue.slice(1, -1);
		}

		return cleanValue;
	}, [ resource?.text ]);

	const request = useCallback(() => {
		log(AMPLITUDE_EVENT.SUGGESTION_GENERATED, { autodocType });
		apiRequest();
	}, [ apiRequest, autodocType, log ]);

	const reset = useCallback(() => {
		setAccepted(false);
		apiReset();
	}, [ apiReset, setAccepted ]);

	const accept = useCallback(() => {
		if (suggestionValue && !accepted) {
			log(AMPLITUDE_EVENT.SUGGESTION_ACCEPTED, { autodocType });
		}
		setAccepted(true);
	}, [ accepted, autodocType, log, suggestionValue ]);

	useEffect(() => {
		if (suggestionValue && !isEmpty) {
			reset();
		}
	}, [ reset, suggestionValue, isEmpty ]);

	useEffect(() => {
		if (response?.status && response?.status !== SuccessStatusCode.Ok) {
			if (response?.status === ErrorOrFailStatusCode.BadGateway) {
				errorNotification(
					NOTIFICATIONS_COPY.ERROR_GET_AUTODOC_SUGGESTION_FAILED(
						AUTODOC_TYPE_LABELS[ autodocType ].toLocaleLowerCase()
					),
					{
						message: NOTIFICATIONS_COPY.ERROR_GET_AUTODOC_SUGGESTION_TITLE
					}
				);
			} else {
				errorNotification(NOTIFICATIONS_COPY.ERROR_GET_AUTODOC_SUGGESTION, {
					message: NOTIFICATIONS_COPY.ERROR_GET_AUTODOC_SUGGESTION_TITLE
				});
			}

			setError(true);
			setTimeout(() => setError(false), 5000);
			reset();
		}
	}, [ reset, response?.status, autodocType, errorNotification ]);

	const isEditing = !enabled || isReadOnly || isUserReadOnly || !isEmpty || accepted;
	const editorValue = suggestionValue && isEmpty ? suggestionValue : value ?? '';

	return {
		accept,
		accepted,
		editorValue,
		hasError,
		isEditing,
		loading,
		request,
		reset,
		value: suggestionValue
	};
}
